'use client';

import { flip, offset, shift } from '@floating-ui/react';
import type { Editor } from '@tiptap/react';
import { useCallback, useState } from 'react';

import { useFloatingElement } from '@/hooks/use-floating-element';

import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import { getSelectionAnchorRect } from '@/components/tiptap-editor/tiptap-ui/trigger-dropdown';

import { TableInsertGridPanel } from './table-insert-grid-panel';
import type { TableInsertType } from './use-table-trigger';

export interface TableInsertTriggerState {
    isOpen: boolean;
    referenceRect: DOMRect | null;
    insertType: TableInsertType;
    open: (editor: Editor, type: TableInsertType) => void;
    close: () => void;
}

export function useTableInsertTrigger(): TableInsertTriggerState {
    const [isOpen, setIsOpen] = useState(false);
    const [referenceRect, setReferenceRect] = useState<DOMRect | null>(null);
    const [insertType, setInsertType] = useState<TableInsertType>('table');

    const open = useCallback((editor: Editor, type: TableInsertType) => {
        queueMicrotask(() => {
            const anchorRect = getSelectionAnchorRect(editor);
            if (!anchorRect) {
                return;
            }

            setInsertType(type);
            setReferenceRect(anchorRect);
            setIsOpen(true);
        });
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
    }, []);

    return {
        isOpen,
        referenceRect,
        insertType,
        open,
        close,
    };
}

export interface TableInsertTriggerDropdownProps {
    editor: Editor;
    isOpen: boolean;
    referenceRect: DOMRect | null;
    insertType: TableInsertType;
    onClose: () => void;
}

export function TableInsertTriggerDropdown({ editor, isOpen, referenceRect, insertType, onClose }: TableInsertTriggerDropdownProps) {
    const handleClose = useCallback(() => {
        onClose();
        editor.chain().focus().run();
    }, [editor, onClose]);

    const { ref, style, getFloatingProps, isMounted } = useFloatingElement(isOpen, referenceRect, 1000, {
        placement: 'bottom-start',
        middleware: [
            offset(10),
            flip({
                mainAxis: true,
                crossAxis: false,
            }),
            shift(),
        ],
        onOpenChange: open => {
            if (!open) {
                handleClose();
            }
        },
    });

    if (!isMounted || !isOpen || !referenceRect) {
        return null;
    }

    const ariaLabel = insertType === 'grid' ? 'Insert grid layout' : insertType === 'zytable' ? 'Insert zyTable' : 'Insert table';

    return (
        <div
            ref={ref}
            style={style}
            {...getFloatingProps()}
            className="tiptap-suggestion-menu"
            data-selector="tiptap-table-insert-trigger-dropdown"
            role="dialog"
            aria-label={ariaLabel}
        >
            <Card className="tiptap-slash-card">
                <CardBody className="tiptap-slash-card-body">
                    <TableInsertGridPanel
                        editor={editor}
                        mode={insertType}
                        focusInputs={isOpen}
                        onInserted={handleClose}
                    />
                </CardBody>
            </Card>
        </div>
    );
}
